import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {NeutralBadge} from "@khanacademy/wonder-blocks-badge";
import Button from "@khanacademy/wonder-blocks-button";
import Clickable from "@khanacademy/wonder-blocks-clickable";
import {View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";

import {ItemCatalogueThumbnail} from "./item-catalogue-thumbnail.tsx";
import type {Swag} from "./types.ts";

import starIcon from "@phosphor-icons/core/fill/star-fill.svg";
import hangerIcon from "@phosphor-icons/core/regular/coat-hanger.svg";

interface Props {
    /** Whether or not a catalogue item is actively being redeemed */
    isRedeemLoading: boolean;
    /** All the available items in the catalogue */
    items: Array<Swag>;
    /** A set containing IDs of the items the user already owns */
    ownedItemIds: Set<string>;
    /** The number of available spending points a user has remaining */
    pointsAvailable?: number;
    /** The current item selected/previewed */
    selectedItem: Swag | null;
    /** Callback that is triggered when the selected item changes */
    onItemSelected: (item: Swag) => unknown;
    /** Callback that is triggered when the user chooses to redeem an item */
    onRedeemItem: (item: Swag) => unknown;
}

/**
 * Renders a gallery of fashion item thumbnails for Khanmigo's closet.
 */
export const ItemCatalogue = (props: Props): React.ReactElement => {
    const {
        isRedeemLoading,
        items,
        ownedItemIds,
        pointsAvailable,
        selectedItem,
        onItemSelected,
        onRedeemItem,
    } = props;

    return (
        <View style={styles.catalogue}>
            {items.map((item, index) => (
                <Clickable
                    testId={item.id}
                    key={item.id}
                    style={styles.catalogueItemWrapper}
                    onClick={() => onItemSelected(item)}
                >
                    {({hovered, focused}) => {
                        const isLocked =
                            !ownedItemIds.has(item.id) &&
                            pointsAvailable != null &&
                            pointsAvailable < item.price;

                        return (
                            <View
                                style={[
                                    styles.catalogueItem,
                                    (hovered ||
                                        focused ||
                                        selectedItem?.id === item.id) &&
                                        styles.catalogueItemFocused,
                                ]}
                            >
                                <ItemCatalogueThumbnail
                                    isLocked={isLocked}
                                    item={item}
                                />
                                <Strut size={spacing.xSmall_8} />
                                {selectedItem?.id === item.id &&
                                !ownedItemIds.has(item.id) &&
                                !isLocked ? (
                                    <Button
                                        size="small"
                                        spinner={isRedeemLoading}
                                        style={styles.ctaBtn}
                                        onClick={() => onRedeemItem(item)}
                                    >
                                        {t`Claim`}
                                    </Button>
                                ) : (
                                    <NeutralBadge
                                        label={getPillLabel(
                                            item,
                                            ownedItemIds.has(item.id),
                                            isLocked,
                                        )}
                                        icon={
                                            !isLocked &&
                                            item.id !== "default" ? (
                                                <PhosphorIcon
                                                    icon={
                                                        ownedItemIds.has(
                                                            item.id,
                                                        )
                                                            ? hangerIcon
                                                            : starIcon
                                                    }
                                                />
                                            ) : undefined
                                        }
                                        showBorder={false}
                                    />
                                )}
                            </View>
                        );
                    }}
                </Clickable>
            ))}
        </View>
    );
};

const getPillLabel = (
    item: Swag,
    isOwned: boolean,
    isLocked: boolean,
): string => {
    if (item.id === "default") {
        return t`Default`;
    }

    if (isOwned) {
        return t`Claimed`;
    }

    if (isLocked) {
        return t`Locked`;
    }

    return item.price.toLocaleString();
};

const GAP_SIZE = spacing.large_24;
const OUTLINE_WIDTH = 2;
const BADGE_HEIGHT = 26;

const styles = StyleSheet.create({
    catalogue: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: GAP_SIZE,
        // So that outlines don't overflow & get cropped, we add 2px padding
        padding: OUTLINE_WIDTH,
    },
    catalogueItemWrapper: {
        flex: "0 1 auto",
        // Display 3 items per row
        width: `calc((100% - 2 * ${GAP_SIZE}px) / 3)`,
        height: "auto",
    },
    catalogueItem: {
        justifyContent: "center",
        alignItems: "center",
        padding: spacing.xSmall_8,
    },
    catalogueItemFocused: {
        outlineStyle: "solid",
        outlineWidth: OUTLINE_WIDTH,
        outlineColor: semanticColor.core.border.instructive.default,
    },
    ctaBtn: {
        height: BADGE_HEIGHT,
    },
});
